import { useState, useEffect, useCallback, useMemo } from 'react';

export interface StepDefinition<TStepId extends string = string> {
  id: TStepId;
  label: string;
  icon?: any;
  [key: string]: any;
}

export interface UseMultiStepFormOptions<TStepId extends string = string> {
  steps: StepDefinition<TStepId>[];
  loadForm: () => Promise<{ metadata: any; answers: any }>;
  saveDraft: (answers: any, stepIndex: number) => Promise<void>;
  getVisibleSteps?: (answers: any, steps: StepDefinition<TStepId>[]) => StepDefinition<TStepId>[];
  validateStep?: (stepId: TStepId, answers: any, metadata: any, extra?: any) => boolean;
  findInitialStep?: (answers: any, steps: StepDefinition<TStepId>[]) => number;
}

export interface UseMultiStepFormResult<TStepId extends string = string> {
  // State
  metadata: any;
  answers: any;
  loading: boolean;
  saving: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  lastSaved: Date | null;
  // Navigation
  currentStep: number;
  currentStepId: TStepId;
  visibleSteps: StepDefinition<TStepId>[];
  isFirstStep: boolean;
  isLastStep: boolean;
  canProceed: boolean;
  nextStep: () => Promise<void>;
  prevStep: () => Promise<void>;
  goToStep: (index: number) => Promise<void>;
  // Updates
  updateAnswers: (path: string, value: any) => void;
  setAnswers: React.Dispatch<React.SetStateAction<any>>;
  setMetadata: React.Dispatch<React.SetStateAction<any>>;
}

/**
 * Multi-step form orchestration hook.
 * Manages loading, saving, step navigation, and validation for wizard-style forms.
 */
export function useMultiStepForm<TStepId extends string = string>(
  options: UseMultiStepFormOptions<TStepId>
): UseMultiStepFormResult<TStepId> {
  const { steps, loadForm, saveDraft, getVisibleSteps, validateStep, findInitialStep } = options;

  const [currentStep, setCurrentStep] = useState(0);
  const [metadata, setMetadata] = useState<any>(null);
  const [answers, setAnswers] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // Load form on mount
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const result = await loadForm();
        if (cancelled) return;

        setMetadata(result.metadata);
        setAnswers(result.answers);

        // Determine initial step
        if (findInitialStep) {
          const computedSteps = getVisibleSteps
            ? getVisibleSteps(result.answers, steps)
            : steps;
          const initialStep = findInitialStep(result.answers, computedSteps);
          if (initialStep > 0) {
            setCurrentStep(initialStep);
          }
        } else if (result.answers?.currentStep && result.answers.currentStep > 0) {
          setCurrentStep(result.answers.currentStep);
        }

        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  // loadForm is expected to be stable (useCallback'd by consumer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Visible steps
  const visibleSteps = useMemo(() => {
    if (getVisibleSteps && answers) {
      return getVisibleSteps(answers, steps);
    }
    return steps;
  }, [steps, answers, getVisibleSteps]);

  const currentStepId = (visibleSteps[currentStep]?.id || steps[0]?.id) as TStepId;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === visibleSteps.length - 1;

  // Save helper
  const saveStep = useCallback(async (stepIndex: number) => {
    if (!answers) return;
    setSaving(true);
    try {
      await saveDraft(answers, stepIndex);
      setLastSaved(new Date());
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  }, [answers, saveDraft]);

  // Navigation
  const nextStep = useCallback(async () => {
    const newStep = Math.min(currentStep + 1, visibleSteps.length - 1);
    await saveStep(newStep);
    setCurrentStep(newStep);
  }, [currentStep, visibleSteps.length, saveStep]);

  const prevStep = useCallback(async () => {
    const newStep = Math.max(currentStep - 1, 0);
    await saveStep(newStep);
    setCurrentStep(newStep);
  }, [currentStep, saveStep]);

  const goToStep = useCallback(async (stepIndex: number) => {
    if (stepIndex < currentStep) {
      await saveStep(stepIndex);
      setCurrentStep(stepIndex);
    }
  }, [currentStep, saveStep]);

  // Update answers helper
  const updateAnswers = useCallback((path: string, value: any) => {
    setAnswers((prev: any) => ({
      ...prev,
      [path]: value,
    }));
  }, []);

  // Validation
  const canProceed = useMemo(() => {
    if (!validateStep) return true;
    return validateStep(currentStepId, answers, metadata);
  }, [validateStep, currentStepId, answers, metadata]);

  return {
    metadata,
    answers,
    loading,
    saving,
    error,
    setError,
    lastSaved,
    currentStep,
    currentStepId,
    visibleSteps,
    isFirstStep,
    isLastStep,
    canProceed,
    nextStep,
    prevStep,
    goToStep,
    updateAnswers,
    setAnswers,
    setMetadata,
  };
}
